/**
 * CaptionOverlay — bottom-center guided-tour caption display.
 *
 * Plan 05-03 Task 2 (NAV-05). Subscribes to useTourStore.activeCaption and
 * renders a period-quiet serif overlay whenever the caption is non-null. The
 * TourRig writes setActiveCaption as its useFrame loop crosses each
 * step.triggerAtT threshold; this component is a passive reader.
 *
 * Chrome: AESTHETIC-BRIEF Rule 7 — Goudy Old Style serif stack on cream
 * #FDF6EE with espresso #3B2F2F text and a 1px warm-ochre #D4A87E border.
 * Mirrors BoardingOverlay / EraToggle / SourcesPanel styling so the caption
 * reads as the same period-quiet chrome family.
 *
 * Position: fixed bottom-center at z-index 30 — above Crosshair (5) and
 * BoundaryFeedback (10), below EraToggle (40) / SourcesPanel (50) / LoadingHUD
 * (100) / BoardingOverlay (150) / DebugHUD (200). The EraToggle never renders
 * during a guided tour (navState !== 'guidedTour' in freeWalk-gated VistaGate
 * flow), so the two bottom-centered chrome elements do not visually collide.
 *
 * Accessibility: role="status" with aria-live="polite" so screen-reader users
 * hear new captions as the tour progresses, without interrupting focus.
 */
import { useTourStore } from '../store/useTourStore';

const SERIF_STACK =
  '"Goudy Old Style", "Cormorant Garamond", Georgia, serif';
const CREAM = '#FDF6EE';
const ESPRESSO = '#3B2F2F';
const WARM_OCHRE = '#D4A87E';

export function CaptionOverlay() {
  const activeCaption = useTourStore((s) => s.activeCaption);

  if (activeCaption === null) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 32,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 30,
        background: CREAM,
        color: ESPRESSO,
        border: `1px solid ${WARM_OCHRE}`,
        fontFamily: SERIF_STACK,
        padding: '14px 28px',
        maxWidth: 640,
        textAlign: 'center',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      }}
    >
      <p
        style={{
          fontFamily: SERIF_STACK,
          fontSize: '1.05rem',
          lineHeight: 1.5,
          margin: 0,
          letterSpacing: '0.01em',
        }}
      >
        {activeCaption}
      </p>
    </div>
  );
}
